import { useMemo, useState } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import {
  type CostChartDayRow,
  type CostChartMonthRow,
  type CostChartProps,
} from '../costChartTypes';
import { resolveCostMethodLabel } from '../costMethodLabels';
import {
  aggregateByDay,
  aggregateByMonth,
  collectMethods,
  isPositiveLimit,
} from './costChartAggregate';
import { CostChartHeader } from './CostChartHeader';
import { CostChartPlot } from './CostChartPlot';
import './CostChart.css';

export type { CostChartDayRow, CostChartMonthRow, CostChartProps };

export function CostChart({
  mode,
  onModeChange,
  fromDay,
  toDay,
  rangeLabel,
  canGoNext,
  onPrev,
  onNext,
  dailyQuery,
  monthlyQuery,
  monthlyCostLimit = null,
  yearlyCostLimit = null,
}: CostChartProps) {
  const { locale, translate } = useI18n();
  const [showMonthlyLimit, setShowMonthlyLimit] = useState(false);
  const [showYearlyLimit, setShowYearlyLimit] = useState(false);

  const hasMonthlyCostLimit = isPositiveLimit(monthlyCostLimit);
  const hasYearlyCostLimit = isPositiveLimit(yearlyCostLimit);
  const showMonthlyLimitLine = hasMonthlyCostLimit && showMonthlyLimit;
  const showYearlyLimitLine =
    mode === 'monthly' && hasYearlyCostLimit && showYearlyLimit;

  const activeQuery = mode === 'daily' ? dailyQuery : monthlyQuery;

  const methods = useMemo(() => {
    if (mode === 'daily') {
      return collectMethods(dailyQuery.data ?? []);
    }
    return collectMethods(monthlyQuery.data ?? []);
  }, [mode, dailyQuery.data, monthlyQuery.data]);

  const methodLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const method of methods) {
      labels[method] = resolveCostMethodLabel(method, translate);
    }
    return labels;
  }, [methods, translate]);

  const chartData = useMemo(() => {
    if (mode === 'daily') {
      if (dailyQuery.isLoading || dailyQuery.isError) return [];
      return aggregateByDay(dailyQuery.data ?? [], fromDay, toDay, methods);
    }
    if (!monthlyQuery.data) return [];
    return aggregateByMonth(monthlyQuery.data, methods);
  }, [
    mode,
    dailyQuery.isLoading,
    dailyQuery.isError,
    dailyQuery.data,
    fromDay,
    toDay,
    monthlyQuery.data,
    methods,
  ]);

  const showRunningTotal = mode === 'daily' || showYearlyLimitLine;

  const yMax = useMemo(() => {
    if (chartData.length === 0) return undefined;
    let max = Math.max(...chartData.map((d) => d.cost));
    if (showRunningTotal) {
      const lastRunning = chartData[chartData.length - 1]?.runningTotal;
      if (lastRunning != null) max = Math.max(max, lastRunning);
    }
    if (showMonthlyLimitLine) max = Math.max(max, monthlyCostLimit);
    if (showYearlyLimitLine) max = Math.max(max, yearlyCostLimit);
    return Math.ceil(max * 1.1);
  }, [
    chartData,
    showRunningTotal,
    showMonthlyLimitLine,
    showYearlyLimitLine,
    monthlyCostLimit,
    yearlyCostLimit,
  ]);

  const runningTotalLabel = translate(
    mode === 'monthly'
      ? 'organisationManagement.costs.chart.yearRunningTotal'
      : 'organisationManagement.costs.chart.runningTotal',
  );

  const yAxisFormatter = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
      notation: 'compact',
    }).format(value);

  const monthlyLimitLabel = translate(
    'organisationManagement.costs.chart.monthlyCostLimit',
  );
  const yearlyLimitLabel = translate(
    'organisationManagement.costs.chart.yearlyCostLimit',
  );

  return (
    <section
      className="cost-chart"
      aria-label={translate('organisationManagement.costs.chart.legend')}
    >
      <CostChartHeader
        title={translate('organisationManagement.costs.chart.title')}
        navLabel={translate('organisationManagement.costs.chart.navLabel')}
        previousLabel={translate('organisationManagement.costs.chart.previous')}
        nextLabel={translate('organisationManagement.costs.chart.next')}
        rangeLabel={rangeLabel}
        canGoNext={canGoNext}
        onPrev={onPrev}
        onNext={onNext}
        mode={mode}
        onModeChange={onModeChange}
        dailyLabel={translate('organisationManagement.costs.chart.daily')}
        monthlyLabel={translate('organisationManagement.costs.chart.monthly')}
        viewToggleLabel={translate(
          'organisationManagement.costs.chart.viewToggleLabel',
        )}
        hasMonthlyCostLimit={hasMonthlyCostLimit}
        hasYearlyCostLimit={hasYearlyCostLimit}
        showMonthlyLimit={showMonthlyLimit}
        showYearlyLimit={showYearlyLimit}
        onShowMonthlyLimitChange={setShowMonthlyLimit}
        onShowYearlyLimitChange={setShowYearlyLimit}
        monthlyLimitLabel={monthlyLimitLabel}
        yearlyLimitLabel={yearlyLimitLabel}
        limitTogglesLabel={translate(
          'organisationManagement.costs.chart.limitTogglesLabel',
        )}
      />

      <div className="cost-chart__body">
        {activeQuery.isLoading ? (
          <p className="cost-chart__status">
            {translate('organisationManagement.costs.loading')}
          </p>
        ) : activeQuery.isError ? (
          <p className="cost-chart__status" role="alert">
            {translate('organisationManagement.costs.loadError')}
          </p>
        ) : chartData.length === 0 ? (
          <p className="cost-chart__status">
            {translate('organisationManagement.costs.empty')}
          </p>
        ) : (
          <CostChartPlot
            mode={mode}
            chartData={chartData}
            methods={methods}
            methodLabels={methodLabels}
            locale={locale}
            yMax={yMax}
            yAxisFormatter={yAxisFormatter}
            showRunningTotal={showRunningTotal}
            runningTotalLabel={runningTotalLabel}
            totalLabel={translate('organisationManagement.costs.chart.total')}
            inputTokensHint={translate(
              'organisationManagement.costs.chart.inputTokensHint',
            )}
            outputTokensHint={translate(
              'organisationManagement.costs.chart.outputTokensHint',
            )}
            showMonthlyLimitLine={showMonthlyLimitLine}
            showYearlyLimitLine={showYearlyLimitLine}
            monthlyCostLimit={monthlyCostLimit}
            yearlyCostLimit={yearlyCostLimit}
            monthlyLimitLabel={monthlyLimitLabel}
            yearlyLimitLabel={yearlyLimitLabel}
          />
        )}
      </div>
    </section>
  );
}
